import { Card, Row, Col, Typography, Statistic, Progress, Timeline, Tag, Space, Pagination, Divider, } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form } from "antd";
import { faClock, faCoffee, faShuffle, faDesktop, faGlobe, faCalendarDay, faArrowLeft, } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { ACTIVITY_SUMMARY_BY_ID, GET_ACTIVITY_APP_NAMES, ACTIVITY_TIMELINE_BY_FILTER } from "../../../Queries/Activity";
import { AntdButton, SelectInput, TimePickerField } from "../../../blueplanitUtils/InputField";
import { useState, useEffect } from "react";
import { setLoader } from "../../../Slices/loaderSlice";
import { useDispatch, useSelector } from "react-redux";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "antd";

const { Title, Text } = Typography;

const formatTime = (seconds = 0) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h ${m}m ${s}s`;
};


const ActivityDayDetails = () => {
  const { id, date } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const employee = useSelector((state) =>
    state.employee.AllEmployees.find(emp => emp.id === id)
  );

  const userName = employee
    ? `${employee.firstname} ${employee.lastname}`
    : "";

  const [currentPage, setCurrentPage] = useState(1);
  const [isSyncing, setIsSyncing] = useState(false);

  const [pageSize, setPageSize] = useState(10); // number of timeline items per page
  const isDevice = location.pathname.includes("/device/");
  const [appliedFilters, setAppliedFilters] = useState({
    appName: null,
    fromTime: null,
    toTime: null,
  });

  // UI filters (editable)
  const [draftFilters, setDraftFilters] = useState({
    appName: "",
    fromTime: null,
    toTime: null,
  });


  const { data, loading, error: summaryError, refetch: refetchSummary, } = useQuery(ACTIVITY_SUMMARY_BY_ID, {
    variables: {
      from: dayjs(date).startOf("day").toISOString(),
      to: dayjs(date).endOf("day").toISOString(),
      ...(isDevice ? { deviceId: id } : { userId: id }),
    },
    fetchPolicy: "network-only",
  });

  const { data: appData, refetch: refetchApps, } = useQuery(GET_ACTIVITY_APP_NAMES, {
    variables: {
      from: dayjs(date).startOf("day").toISOString(),
      to: dayjs(date).endOf("day").toISOString(),
      ...(isDevice ? { deviceId: id } : { userId: id }),
    },
    fetchPolicy: "network-only",
  });


  const {
    data: filteredTimelineData, loading: timelineLoading, error: timelineError, refetch: refetchTimeline,
  } = useQuery(ACTIVITY_TIMELINE_BY_FILTER, {
    variables: {
      from: dayjs(date).startOf("day").toISOString(),
      to: dayjs(date).endOf("day").toISOString(),
      ...(isDevice ? { deviceId: id } : { userId: id }),

      appName: appliedFilters.appName || null,
      fromTime: appliedFilters.fromTime,
      toTime: appliedFilters.toTime,
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    dispatch(setLoader(loading));
    dispatch(setLoader(timelineLoading));
  }, [loading, timelineLoading]);

  const appOptions =
    appData?.getActivityAppNames?.map((name) => ({
      label: name,
      value: name,
    })) || [];
  const summary = data?.activitySummaryById?.response;

  const totalTime =
    summary?.totalActive + summary?.totalIdle;

  const getValidDomainGroups = (domainGroups = {}) =>
    Object.entries(domainGroups).filter(
      ([key, value]) =>
        key !== "__typename" && typeof value === "number"
    );

  const getPercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  const timelineToShow =
    filteredTimelineData?.activityTimelineByFilter || [];

  const paginatedTimeline = timelineToShow.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const handleSearch = () => {
    setAppliedFilters({
      appName: draftFilters.appName || null,
      fromTime: draftFilters.fromTime,
      toTime: draftFilters.toTime,
    });
    setCurrentPage(1);
  };


  const handleClear = () => {
    setDraftFilters({
      appName: "",
      fromTime: null,
      toTime: null,
    });

    setAppliedFilters({
      appName: null,
      fromTime: null,
      toTime: null,
    });

    setCurrentPage(1);
  };
  const handleSync = async () => {
    if (isSyncing) return;

    try {
      setIsSyncing(true);

      await Promise.all([
        refetchSummary(),
        refetchApps(),
        refetchTimeline(),
      ]);
    } catch (err) {
      console.error("Sync failed", err);
    } finally {
      setIsSyncing(false);
    }
  };



  return (
    <div style={{ padding: 20 }}>
      {/* ================= HEADER ================= */}
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="center"
      >
        <AntdButton
          type="link"
          label="Back"
          icon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        />

        <Tooltip title="Sync Now">
          <FontAwesomeIcon
            icon={faSync}
            spin={isSyncing}
            onClick={handleSync}
            style={{
              fontSize: "20px",
              color: "#1890ff",
              cursor: "pointer"
            }}
          />
        </Tooltip>
      </Space>

      {!loading && summaryError && (
        <Text type="danger">
          Failed to load activity summary
        </Text>
      )}

      {!loading && !summaryError && !summary && (
        <Text>No activity data available for this day</Text>
      )}
      {summary && (
        <>

          <Card>
            <Space direction="vertical" size={2}>
              <Title level={3} style={{ margin: 0 }}>
                {userName}
              </Title>

              <Text type="secondary">
                <FontAwesomeIcon icon={faCalendarDay} style={{ marginRight: 6 }} />
                {dayjs(date).format("DD MMM YYYY")}
              </Text>
            </Space>

            <Divider style={{ margin: "12px 0" }} />

            <Row gutter={24}>
              <Col span={6}>
                <Statistic
                  title="Active Time"
                  value={formatTime(summary.totalActive)}
                  prefix={<FontAwesomeIcon icon={faClock} />}
                />
              </Col>

              <Col span={6}>
                <Statistic
                  title="Idle Time"
                  value={formatTime(summary.totalIdle)}
                  prefix={<FontAwesomeIcon icon={faCoffee} />}
                />
              </Col>

              <Col span={6}>
                <Statistic
                  title="Focus Switches"
                  value={summary.focusSwitches}
                  prefix={<FontAwesomeIcon icon={faShuffle} />}
                />
              </Col>

              <Col span={6}>
                <Statistic
                  title="Breaks"
                  value={summary.breakCount}
                  prefix={<FontAwesomeIcon icon={faCoffee} />}
                />
              </Col>
            </Row>
          </Card>

          {/* ================= CATEGORY SPLIT ================= */}
          <Card title="Category Split" style={{ marginTop: 16 }}>
            {getValidDomainGroups(summary.domainGroups).length === 0 ? (
              <Text type="secondary">No category data</Text>
            ) : (
              getValidDomainGroups(summary.domainGroups).map(
                ([key, value]) => (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <Row justify="space-between">
                      <Text strong>{key}</Text>
                      <Text>{formatTime(value)}</Text>
                    </Row>

                    <Progress
                      percent={getPercentage(value, totalTime)}
                      showInfo={false}
                    />
                  </div>
                )
              )
            )}
          </Card>


          {/* ================= APP USAGE ================= */}
          <Card
            title={
              <>
                <FontAwesomeIcon icon={faDesktop} /> App Usage
              </>
            }
            style={{ marginTop: 16 }}
          >
            <CustomGridTable
              size="small"
              pagination={false}
              dataSource={summary.apps}
              rowKey="name"
              columns={[
                {
                  title: "Application",
                  dataIndex: "name",
                },
                {
                  title: "Time Spent",
                  dataIndex: "duration",
                  render: (v) => formatTime(v),
                  align: "right",
                },
              ]}
            />
          </Card>

          {/* ================= BROWSER USAGE ================= */}
          <Card
            title={
              <>
                <FontAwesomeIcon icon={faGlobe} /> Browser Usage
              </>
            }
            style={{ marginTop: 16 }}
          >
            <CustomGridTable
              size="small"
              pagination={false}
              dataSource={summary.browser}
              rowKey="domain"
              columns={[
                {
                  title: "Domain",
                  dataIndex: "domain",
                },
                {
                  title: "Time Spent",
                  dataIndex: "duration",
                  render: (v) => formatTime(v),
                  align: "right",
                },
              ]}
            />
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Form layout="vertical">
              <Row gutter={16} align="middle">
                {/* App Name */}
                <Col span={6}>
                  <Form.Item label="App Name" style={{ marginBottom: 0 }}>
                    <SelectInput
                      placeholder="Select App"
                      allowClear
                      showSearch
                      options={appOptions}
                      value={draftFilters.appName || undefined}
                      onChange={(value) =>
                        setDraftFilters({ ...draftFilters, appName: value || "" })
                      }
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />

                  </Form.Item>
                </Col>

                {/* From Time */}
                <Col span={5}>
                  <Form.Item label="From Time" style={{ marginBottom: 0 }}>
                    <TimePickerField
                      format="HH:mm"
                      style={{ width: "100%" }}
                      value={
                        draftFilters.fromTime
                          ? dayjs(draftFilters.fromTime, "HH:mm")
                          : null
                      }
                      onChange={(t) =>
                        setDraftFilters({
                          ...draftFilters,
                          fromTime: t ? t.format("HH:mm") : null,
                        })
                      }
                    />
                  </Form.Item>
                </Col>

                {/* To Time */}
                <Col span={5}>
                  <Form.Item label="To Time" style={{ marginBottom: 0 }}>
                    <TimePickerField
                      format="HH:mm"
                      style={{ width: "100%" }}
                      value={
                        draftFilters.toTime
                          ? dayjs(draftFilters.toTime, "HH:mm")
                          : null
                      }
                      onChange={(t) =>
                        setDraftFilters({
                          ...draftFilters,
                          toTime: t ? t.format("HH:mm") : null,
                        })
                      }
                    />
                  </Form.Item>
                </Col>

                {/* Buttons */}
                <Col span={8} style={{ marginTop: 35 }} >
                  <Form.Item>
                    <Space>
                      <AntdButton
                        variant="outlined"
                        type="primary"
                        label="Search"
                        onClick={handleSearch}
                        style={{ backgroundColor: "#007bff", color: "white" }}
                      />
                      <AntdButton
                        variant="outlined"
                        label="Clear"
                        onClick={handleClear}
                        type="primary"
                        color="success"
                        style={{ backgroundColor: "#837F7F", color: "white" }}
                      />
                    </Space>
                  </Form.Item>
                </Col>

              </Row>
            </Form>
          </Card>

          {/* ================= TIMELINE ================= */}
          <Card title="Activity Timeline" style={{ marginTop: 16 }}>
            {timelineError && (
              <Text type="danger">
                Failed to load timeline data
              </Text>
            )}

            {!timelineError && timelineToShow.length === 0 && (
              <Text type="secondary">
                No activity found for selected filters
              </Text>
            )}

            {!timelineError && timelineToShow.length > 0 && (
              <>
                <Timeline>
                  {paginatedTimeline.map((item, idx) => (
                    <Timeline.Item key={idx}>
                      <Row gutter={8}>
                        <Col span={6}>
                          <Tag color="blue">{item.appName}</Tag>
                        </Col>
                        <Col span={10}>
                          <Text ellipsis>
                            {item.windowTitle || "—"}
                          </Text>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary">
                            {dayjs(Number(item.startTime)).format("HH:mm:ss")}
                            {" → "}
                            {dayjs(Number(item.endTime)).format("HH:mm:ss")}
                            {" · "}
                            {formatTime(item.duration)}
                          </Text>
                        </Col>
                      </Row>
                    </Timeline.Item>
                  ))}
                </Timeline>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={timelineToShow.length}
                  showSizeChanger
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                />
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default ActivityDayDetails;